/**
 * Global combat tunables (DECISIONS.md #24: every undefined number is a default in data, tunable
 * from the admin panel later). Per-mech and per-weapon numbers live in their own JSON files.
 */
import { z } from 'zod';
const finite = z.number().finite();
const positive = finite.positive();
const nonNegative = finite.nonnegative();
const fraction = finite.min(0).max(1);
const wholeNonNegative = z.number().int().nonnegative();
export const TIER_NAMES = ['bronze', 'silver', 'gold', 'platinum', 'diamond'];
export const BalanceSchema = z
    .object({
    /** Seconds without damage before the shield regenerates (BUILD_PLAN section 7). */
    shieldRegenDelaySeconds: positive,
    /** Weapon lockout after reaching the heat cap. */
    overheatLockSeconds: positive,
    /** Heat dissipation multiplier while venting (weapons locked meanwhile). */
    ventDissipationScale: positive,
    /** Height of the aim origin and default muzzle as a fraction of capsule height. */
    aimHeightFraction: fraction,
    hitLocation: z
        .object({
        /** Hits below this fraction of the height count as legs. */
        legBand: fraction,
        /** Torso-band hits laterally outside this fraction of the radius count as arms. */
        armLateralFraction: fraction,
        /** Hits from within this arc behind the facing count as rear, degrees. */
        rearArcDeg: positive,
        rearCritMultiplier: positive,
    })
        .strict(),
    armor: z
        .object({
        /** Armor is clamped here even with buffs. */
        max: fraction,
    })
        .strict(),
    legDamage: z
        .object({
        /** Largest speed reduction from leg damage. */
        maxSlow: fraction,
        /** Leg damage equal to this fraction of max HP reaches the largest slow. */
        atHpFraction: positive,
    })
        .strict(),
    armDamage: z
        .object({
        /** Largest spread increase from arm damage (0.6 = +60 %). */
        maxSpread: nonNegative,
        atHpFraction: positive,
    })
        .strict(),
    melee: z
        .object({
        /** Every mech carries this melee weapon unless its data overrides it (DECISIONS.md #15). */
        defaultWeapon: z.string().min(1),
    })
        .strict(),
    ultimate: z
        .object({
        perDamageDealt: nonNegative,
        perDamageTaken: nonNegative,
        perSecond: nonNegative,
    })
        .strict(),
    suddenDeath: z
        .object({
        /** Fraction of max HP drained per second once sudden death starts. */
        drainPerSecond: positive,
    })
        .strict(),
    scoring: z
        .object({
        roundWin: nonNegative,
        matchWin: nonNegative,
        accuracyBonusMax: nonNegative,
        /** Accuracy at which the bonus starts and at which it maxes out. */
        accuracyLow: fraction,
        accuracyHigh: fraction,
        timeBonusPerRound: nonNegative,
        firstBlood: nonNegative,
        xpDivisor: positive,
        credits: z
            .object({
            base: nonNegative,
            perRoundWin: nonNegative,
            perMatchWin: nonNegative,
            modeScale: z
                .object({
                practice: nonNegative,
                botFill: nonNegative,
                casual: nonNegative,
                ranked: nonNegative,
            })
                .strict(),
        })
            .strict(),
    })
        .strict(),
    /** Anti-collusion payout rules (DECISIONS.md #22, #23, #89). */
    economy: z
        .object({
        /** A match decided by rounds pays only a seat that dealt at least this much damage. */
        minDamageForPayout: nonNegative,
        /** Credits an account can earn from matches per UTC day. */
        dailyCreditCap: wholeNonNegative,
        /** Ranked rating gains against the same opponent shrink after these meetings. */
        repeatOpponent: z
            .object({
            windowHours: positive,
            /** Meetings within the window that still earn the full gain. */
            freeMeetings: wholeNonNegative,
            /** Scale applied to gains from the next meeting on. */
            gainScale: fraction,
        })
            .strict(),
    })
        .strict(),
    /** XP and levels (BUILD_PLAN section 7: xpToNext = 500 × level^1.4; DECISIONS.md #25). */
    progression: z
        .object({
        levelCap: z.number().int().min(1),
        xpBase: positive,
        xpExponent: positive,
    })
        .strict(),
    /** Glicko-2 (BUILD_PLAN section 7) with the tiers of DECISIONS.md #25. */
    rating: z
        .object({
        initial: positive,
        initialRd: positive,
        initialVolatility: positive,
        /** System constant constraining volatility change over time (0.3–1.2 is usual). */
        tau: positive,
        /** The deviation never settles below this: veterans keep moving. */
        minRd: positive,
        /** The deviation never grows past this: inactivity stops widening eventually. */
        maxRd: positive,
        /** Ranked matches before a rating shows on the board and carries a tier. */
        placementMatches: wholeNonNegative,
        /** The deviation must also have settled below this to count as placed. */
        placementMaxRd: positive,
        /** Ascending bands; the last band with `min` at or below the rating names the tier. */
        tiers: z.array(z.object({ name: z.enum(TIER_NAMES), min: nonNegative }).strict()).min(1),
    })
        .strict(),
})
    .strict();
//# sourceMappingURL=balance-schema.js.map